import {
  Business,
  Edit,
  Security,
  SwapHoriz,
  CloudUpload,
  Delete,
  Image,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  LinearProgress,
} from '@mui/material';
import { getCurrentOrgId, getProfile, getOrganization } from 'client/modules/Auth/AuthReducer';
import callApi from 'client/util/apiCaller';
import {
  fetchOrganization,
  fetchOrganizationMembers,
  updateOrganization,
  updateOrganizationSuccess,
  generateLogoUploadUrl,
  updateOrganizationLogo,
  deleteOrganizationLogo,
} from 'client/modules/Organizations/OrganizationActions';
import React, { useCallback, useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

const messages = defineMessages({
  title: {
    id: 'General.Title',
    defaultMessage: 'General Settings',
  },
  organizationName: {
    id: 'General.OrganizationName',
    defaultMessage: 'Organization name',
  },
  organizationOwner: {
    id: 'General.OrganizationOwner',
    defaultMessage: 'Organization owner',
  },
  transferOwnership: {
    id: 'General.TransferOwnership',
    defaultMessage: 'Transfer ownership',
  },
  edit: {
    id: 'General.Edit',
    defaultMessage: 'Edit',
  },
  save: {
    id: 'General.Save',
    defaultMessage: 'Save',
  },
  cancel: {
    id: 'General.Cancel',
    defaultMessage: 'Cancel',
  },
  privacyAndSecurity: {
    id: 'General.PrivacyAndSecurity',
    defaultMessage: 'Privacy and Security',
  },
  termsOfService: {
    id: 'General.TermsOfService',
    defaultMessage: 'Terms of Service',
  },
  privacyPolicy: {
    id: 'General.PrivacyPolicy',
    defaultMessage: 'Privacy Policy',
  },
  dataProcessingAgreement: {
    id: 'General.DataProcessingAgreement',
    defaultMessage: 'Data Processing Agreement',
  },
  cookiesPolicy: {
    id: 'General.CookiesPolicy',
    defaultMessage: 'Cookies Policy',
  },

  transferOwnershipDialog: {
    id: 'General.TransferOwnershipDialog',
    defaultMessage: 'Transfer ownership',
  },
  transferOwnershipDescription: {
    id: 'General.TransferOwnershipDescription',
    defaultMessage:
      'Transferring ownership will make another user the owner of this organization. You will become an admin member.',
  },
  selectNewOwner: {
    id: 'General.SelectNewOwner',
    defaultMessage: 'Select new owner',
  },
  confirmTransfer: {
    id: 'General.ConfirmTransfer',
    defaultMessage: 'Transfer ownership',
  },
  verifyEmail: {
    id: 'General.VerifyEmail',
    defaultMessage: 'Verify email',
  },
  verificationCode: {
    id: 'General.VerificationCode',
    defaultMessage: 'Verification code',
  },
  verificationCodeDescription: {
    id: 'General.VerificationCodeDescription',
    defaultMessage:
      'Enter the verification code we sent to your email address to confirm the ownership transfer.',
  },
  sendVerificationCode: {
    id: 'General.SendVerificationCode',
    defaultMessage: 'Send verification code',
  },
  verificationCodeSent: {
    id: 'General.VerificationCodeSent',
    defaultMessage: 'Verification code sent to your email address',
  },
  invalidVerificationCode: {
    id: 'General.InvalidVerificationCode',
    defaultMessage: 'Invalid verification code. Please try again.',
  },
  ownershipTransferred: {
    id: 'General.OwnershipTransferred',
    defaultMessage: 'Ownership has been transferred successfully',
  },
  teammateRequired: {
    id: 'General.TeammateRequired',
    defaultMessage:
      'You can only transfer ownership to an existing teammate. Please invite them to your organization first.',
  },
  errorLoadingOrganization: {
    id: 'General.ErrorLoadingOrganization',
    defaultMessage: 'Error loading organization information',
  },
  organizationNameUpdated: {
    id: 'General.OrganizationNameUpdated',
    defaultMessage: 'Organization name updated successfully',
  },
  errorUpdatingName: {
    id: 'General.ErrorUpdatingName',
    defaultMessage: 'Error updating organization name',
  },
  unknownUser: {
    id: 'General.UnknownUser',
    defaultMessage: 'Unknown user',
  },
  noOwnerAssigned: {
    id: 'General.NoOwnerAssigned',
    defaultMessage: 'No owner assigned',
  },

  // Logo messages
  organizationLogo: {
    id: 'General.OrganizationLogo',
    defaultMessage: 'Organization logo',
  },
  uploadLogo: {
    id: 'General.UploadLogo',
    defaultMessage: 'Upload logo',
  },
  changeLogo: {
    id: 'General.ChangeLogo',
    defaultMessage: 'Change logo',
  },
  removeLogo: {
    id: 'General.RemoveLogo',
    defaultMessage: 'Remove logo',
  },
  logoUploadDescription: {
    id: 'General.LogoUploadDescription',
    defaultMessage:
      'Upload a PNG or GIF image. Maximum file size: 30 KB. Recommended size: 320x132 pixels.',
  },
  logoUploaded: {
    id: 'General.LogoUploaded',
    defaultMessage: 'Logo uploaded successfully',
  },
  logoRemoved: {
    id: 'General.LogoRemoved',
    defaultMessage: 'Logo removed successfully',
  },
  errorUploadingLogo: {
    id: 'General.ErrorUploadingLogo',
    defaultMessage: 'Error uploading logo',
  },
  errorRemovingLogo: {
    id: 'General.ErrorRemovingLogo',
    defaultMessage: 'Error removing logo',
  },
  invalidFileType: {
    id: 'General.InvalidFileType',
    defaultMessage: 'Invalid file type. Only PNG and GIF files are allowed.',
  },
  fileTooLarge: {
    id: 'General.FileTooLarge',
    defaultMessage: 'File is too large. Maximum size is 30 KB.',
  },
  currentLogo: {
    id: 'General.CurrentLogo',
    defaultMessage: 'Current logo:',
  },
  organizationLogoAlt: {
    id: 'General.OrganizationLogoAlt',
    defaultMessage: 'Organization logo',
  },
  noLogoUploaded: {
    id: 'General.NoLogoUploaded',
    defaultMessage: 'No logo uploaded',
  },
  uploadingProgress: {
    id: 'General.UploadingProgress',
    defaultMessage: 'Uploading... {progress}%',
  },
});

function GeneralPage() {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const profile = useSelector(getProfile);
  const currentOrgId = useSelector(getCurrentOrgId);
  const organization = useSelector(getOrganization);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferStep, setTransferStep] = useState(0); // 0: select user, 1: verify email
  const [selectedNewOwner, setSelectedNewOwner] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);

  // Logo upload state
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUploadProgress, setLogoUploadProgress] = useState(0);

  const loadOrganization = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch organization data into Redux and members separately
      const [orgResponse, membersResponse] = await Promise.all([
        dispatch(fetchOrganization(null, currentOrgId)),
        dispatch(fetchOrganizationMembers(currentOrgId, null)),
      ]);

      setMembers(membersResponse.members || []);

      // Set initial name from the fetched organization
      if (orgResponse.organization?.name) {
        setNewName(orgResponse.organization.name);
      }
    } catch (err) {
      setError(formatMessage(messages.errorLoadingOrganization));
      console.error('Error loading organization:', err);
    } finally {
      setLoading(false);
    }
  }, [dispatch, currentOrgId, formatMessage]);

  useEffect(() => {
    if (currentOrgId) {
      loadOrganization();
    }
  }, [currentOrgId, loadOrganization]);

  useEffect(() => {
    if (organization?.name) {
      setNewName(organization.name);
    }
  }, [organization?.name]);

  const handleEditName = () => {
    setEditingName(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancelEdit = () => {
    setEditingName(false);
    setNewName(organization?.name || '');
  };

  const handleSaveName = async () => {
    try {
      setSaving(true);
      setError(null);

      await dispatch(
        updateOrganization(null, currentOrgId, {
          name: newName.trim(),
        }),
      );

      setEditingName(false);
      setSuccess(formatMessage(messages.organizationNameUpdated));
    } catch (err) {
      setError(formatMessage(messages.errorUpdatingName));
      console.error('Error updating organization name:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleTransferOwnership = () => {
    setTransferDialogOpen(true);
    setTransferStep(0);
    setSelectedNewOwner('');
    setVerificationCode('');
    setError(null);
  };

  const handleSendVerificationCode = async () => {
    if (!selectedNewOwner) {
      setError('Please select a new owner');
      return;
    }

    try {
      setTransferLoading(true);
      await callApi(
        dispatch,
        `organizations/${currentOrgId}/transfer-ownership/verify`,
        null,
        'POST',
        {
          newOwnerId: selectedNewOwner,
        },
      );
      setTransferStep(1);
      setSuccess(formatMessage(messages.verificationCodeSent));
    } catch (err) {
      setError(err.message || 'Failed to send verification code');
    } finally {
      setTransferLoading(false);
    }
  };

  const handleConfirmTransfer = async () => {
    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    try {
      setTransferLoading(true);
      const response = await callApi(
        dispatch,
        `organizations/${currentOrgId}/transfer-ownership/confirm`,
        null,
        'POST',
        {
          newOwnerId: selectedNewOwner,
          verificationCode: verificationCode.trim(),
        },
      );

      // Update Redux store with new organization data
      if (response.organization) {
        dispatch(updateOrganizationSuccess(response));
      }

      setSuccess(formatMessage(messages.ownershipTransferred));
      setTransferDialogOpen(false);

      // Reload members since ownership change affects member roles
      await loadOrganization();
    } catch (_err) {
      setError(formatMessage(messages.invalidVerificationCode));
    } finally {
      setTransferLoading(false);
    }
  };

  const handleCloseTransferDialog = () => {
    setTransferDialogOpen(false);
    setTransferStep(0);
    setSelectedNewOwner('');
    setVerificationCode('');
    setError(null);
    setSuccess(null);
  };

  const getCurrentUserRole = () => {
    if (!organization || !profile?.user_id) return null;
    const member = organization.members?.find(
      m => m.user === profile.user_id || m.user?._id === profile.user_id,
    );
    return member?.role;
  };

  const getOwnerInfo = () => {
    if (!organization) return null;
    const owner = organization.members?.find(m => m.role === 'owner');
    return owner;
  };

  const isCurrentUserOwner = () => {
    return getCurrentUserRole() === 'owner';
  };

  // Logo upload functions
  const validateFile = file => {
    // Check file type
    if (!['image/png', 'image/gif'].includes(file.type)) {
      setError(formatMessage(messages.invalidFileType));
      return false;
    }

    // Check file size (30 KB = 30 * 1024 bytes)
    if (file.size > 30 * 1024) {
      setError(formatMessage(messages.fileTooLarge));
      return false;
    }

    return true;
  };

  const handleLogoUpload = async event => {
    const file = event.target.files[0];
    if (!file || !validateFile(file)) {
      return;
    }

    try {
      setLogoUploading(true);
      setLogoUploadProgress(0);
      setError(null);

      // Get signed URL for upload using Redux action
      const signResponse = await dispatch(
        generateLogoUploadUrl(currentOrgId, file.type, file.name),
      );

      const { signedUrl, logoUrl, filename } = signResponse;

      // Upload file to S3
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = e => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          setLogoUploadProgress(progress);
        }
      };

      xhr.onload = async () => {
        if (xhr.status === 200) {
          try {
            // Update organization with logo URL using Redux action
            await dispatch(updateOrganizationLogo(currentOrgId, logoUrl, filename));

            setSuccess(formatMessage(messages.logoUploaded));
          } catch (_updateError) {
            setError(formatMessage(messages.errorUploadingLogo));
          }
        } else {
          setError(formatMessage(messages.errorUploadingLogo));
        }
        setLogoUploading(false);
        setLogoUploadProgress(0);
      };

      xhr.onerror = () => {
        setError(formatMessage(messages.errorUploadingLogo));
        setLogoUploading(false);
        setLogoUploadProgress(0);
      };

      xhr.open('PUT', signedUrl, true);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    } catch (_err) {
      setError(formatMessage(messages.errorUploadingLogo));
      setLogoUploading(false);
      setLogoUploadProgress(0);
    }
  };

  const handleLogoRemove = async () => {
    try {
      setLogoUploading(true);
      setError(null);

      await dispatch(deleteOrganizationLogo(currentOrgId));

      setSuccess(formatMessage(messages.logoRemoved));
    } catch (_err) {
      setError(formatMessage(messages.errorRemovingLogo));
    } finally {
      setLogoUploading(false);
    }
  };

  if (loading) {
    return (
      <Paper style={{ padding: '2em' }}>
        <Typography variant="h5" component="h2" gutterBottom>
          {formatMessage(messages.title)}
        </Typography>
        <Typography>Loading...</Typography>
      </Paper>
    );
  }

  if (error && !organization) {
    return (
      <Paper style={{ padding: '2em' }}>
        <Typography variant="h5" component="h2" gutterBottom>
          {formatMessage(messages.title)}
        </Typography>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  const owner = getOwnerInfo();

  return (
    <Paper style={{ padding: '2em', marginBottom: '2em' }}>
      <Helmet title={formatMessage(messages.title)} />

      <Typography variant="h5" component="h2" gutterBottom>
        {formatMessage(messages.title)}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Organization Name */}
        <Card variant="outlined">
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Business sx={{ color: 'text.secondary' }} />
                <Typography variant="h6" component="h3">
                  {formatMessage(messages.organizationName)}
                </Typography>
              </Box>
              {!editingName && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<Edit />}
                  onClick={handleEditName}
                  disabled={!isCurrentUserOwner()}
                >
                  {formatMessage(messages.edit)}
                </Button>
              )}
            </Box>

            {editingName ? (
              <Box sx={{ mt: 2 }} component="form">
                <TextField
                  fullWidth
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  variant="outlined"
                  size="small"
                  disabled={saving}
                />
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="small"
                    onClick={handleSaveName}
                    disabled={saving || !newName.trim() || newName.trim() === organization?.name}
                  >
                    {formatMessage(messages.save)}
                  </Button>
                  <Button variant="text" size="small" onClick={handleCancelEdit} disabled={saving}>
                    {formatMessage(messages.cancel)}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Typography variant="body1" sx={{ mt: 1, fontSize: '1.1rem' }}>
                {organization?.name || 'Unnamed Organization'}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Organization Logo */}
        <Card variant="outlined">
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Image sx={{ color: 'text.secondary' }} />
                <Typography variant="h6" component="h3">
                  {formatMessage(messages.organizationLogo)}
                </Typography>
              </Box>
            </Box>

            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              {formatMessage(messages.logoUploadDescription)}
            </Typography>

            {organization?.logo?.url ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  {formatMessage(messages.currentLogo)}
                </Typography>
                <Box
                  sx={{
                    display: 'inline-block',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    backgroundColor: 'background.default',
                  }}
                >
                  <img
                    src={organization.logo.url}
                    alt={formatMessage(messages.organizationLogoAlt)}
                    style={{
                      maxWidth: '320px',
                      maxHeight: '132px',
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{ mb: 2, fontStyle: 'italic', color: 'text.secondary' }}
              >
                {formatMessage(messages.noLogoUploaded)}
              </Typography>
            )}

            {logoUploading && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {formatMessage(messages.uploadingProgress, { progress: logoUploadProgress })}
                </Typography>
                <LinearProgress variant="determinate" value={logoUploadProgress} />
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                component="label"
                startIcon={organization?.logo?.url ? <Edit /> : <CloudUpload />}
                disabled={
                  logoUploading || (!isCurrentUserOwner() && getCurrentUserRole() !== 'admin')
                }
              >
                {organization?.logo?.url
                  ? formatMessage(messages.changeLogo)
                  : formatMessage(messages.uploadLogo)}
                <input
                  type="file"
                  accept="image/png,image/gif"
                  onChange={handleLogoUpload}
                  hidden
                  style={{ display: 'none' }}
                />
              </Button>

              {organization?.logo?.url && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={handleLogoRemove}
                  disabled={
                    logoUploading || (!isCurrentUserOwner() && getCurrentUserRole() !== 'admin')
                  }
                >
                  {formatMessage(messages.removeLogo)}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Organization Owner */}
        <Card variant="outlined">
          <CardContent>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                  {formatMessage(messages.organizationOwner)}
                </Typography>
                {owner ? (
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {owner.user?.email || owner.user || formatMessage(messages.unknownUser)}
                  </Typography>
                ) : (
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    {formatMessage(messages.noOwnerAssigned)}
                  </Typography>
                )}
              </Box>

              {isCurrentUserOwner() && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<SwapHoriz />}
                  onClick={handleTransferOwnership}
                >
                  {formatMessage(messages.transferOwnership)}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        <Divider sx={{ my: 2 }} />

        {/* Privacy and Security */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Security sx={{ color: 'text.secondary' }} />
            <Typography variant="h6" component="h3">
              {formatMessage(messages.privacyAndSecurity)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Link href="/legal/terms" target="_blank" underline="hover">
              {formatMessage(messages.termsOfService)}
            </Link>
            <Link href="/legal/privacy" target="_blank" underline="hover">
              {formatMessage(messages.privacyPolicy)}
            </Link>

            <Link href="/legal/dpa" target="_blank" underline="hover">
              {formatMessage(messages.dataProcessingAgreement)}
            </Link>
            <Link href="/legal/cookies" target="_blank" underline="hover">
              {formatMessage(messages.cookiesPolicy)}
            </Link>
          </Box>
        </Box>
      </Box>

      {/* Transfer Ownership Dialog */}
      <Dialog open={transferDialogOpen} onClose={handleCloseTransferDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{formatMessage(messages.transferOwnershipDialog)}</DialogTitle>
        <DialogContent>
          <Stepper activeStep={transferStep} sx={{ mb: 3 }}>
            <Step>
              <StepLabel>{formatMessage(messages.selectNewOwner)}</StepLabel>
            </Step>
            <Step>
              <StepLabel>{formatMessage(messages.verifyEmail)}</StepLabel>
            </Step>
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && transferStep === 1 && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {transferStep === 0 && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {formatMessage(messages.transferOwnershipDescription)}
              </Typography>

              {members.filter(m => m.role !== 'owner' && m.status === 'accepted').length === 0 ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {formatMessage(messages.teammateRequired)}
                </Alert>
              ) : (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>{formatMessage(messages.selectNewOwner)}</InputLabel>
                  <Select
                    value={selectedNewOwner}
                    onChange={e => setSelectedNewOwner(e.target.value)}
                    label={formatMessage(messages.selectNewOwner)}
                  >
                    {members
                      .filter(m => m.role !== 'owner' && m.status === 'accepted' && m.user)
                      .map(member => (
                        <MenuItem
                          key={member.user._id || member.user}
                          value={member.user._id || member.user}
                        >
                          {member.user.email || member.user} ({member.role})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          )}

          {transferStep === 1 && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {formatMessage(messages.verificationCodeDescription)}
              </Typography>
              <TextField
                fullWidth
                label={formatMessage(messages.verificationCode)}
                value={verificationCode}
                onChange={e => setVerificationCode(e.target.value)}
                placeholder="Enter 6-digit code"
                inputProps={{ maxLength: 6 }}
                sx={{ mb: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTransferDialog} disabled={transferLoading}>
            {formatMessage(messages.cancel)}
          </Button>

          {transferStep === 0 && (
            <Button
              onClick={handleSendVerificationCode}
              variant="contained"
              disabled={!selectedNewOwner || transferLoading}
              startIcon={transferLoading ? <CircularProgress size={20} /> : null}
            >
              {formatMessage(messages.sendVerificationCode)}
            </Button>
          )}

          {transferStep === 1 && (
            <Button
              onClick={handleConfirmTransfer}
              variant="contained"
              disabled={!verificationCode.trim() || transferLoading}
              startIcon={transferLoading ? <CircularProgress size={20} /> : null}
            >
              {formatMessage(messages.confirmTransfer)}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default GeneralPage;
